import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import React, { useEffect } from "react";
import { DynamicSupplyHardware } from "@/types";
import { HARDWARE_MAP } from "@/utils/mapping";
import { CountryFlagLabel } from "@/components/Common/CountryFlagLabel";
import { COUNTRIES } from "@/constants/map";
import { VerticalFlowAnalytics } from "./analytics";
import { ConfidentialComputeBadge } from "./components/ConfidentialComputeBadge";

type HardwareDetailsModalProps = {
  isOpen: boolean;
  hardware: DynamicSupplyHardware | null;
  onClose: () => void;
  analytics?: Pick<VerticalFlowAnalytics, "trackGPUMoreDetailsClicked">;
  hiddenHardwareFields: string[];
};

export const HardwareDetailsModal: React.FC<HardwareDetailsModalProps> = ({
  isOpen,
  hardware,
  onClose,
  analytics,
  hiddenHardwareFields = []
}) => {
  useEffect(() => {
    if (!hardware) return;
    const location = COUNTRIES.find(
      (country) => country.countryCode === hardware.location
    )?.countryName;
    analytics?.trackGPUMoreDetailsClicked({
      name: hardware.hardwareName,
      price: hardware.price,
      location: location,
      supplier: hardware.supplier === "internal" ? "IO" : "Partner"
    });
  }, [hardware, analytics]);
  if (!hardware) return null;

  const discount =
    hardware.oldPrice && hardware.oldPrice > hardware.price
      ? Math.round(((hardware.oldPrice - hardware.price) / hardware.oldPrice) * 100)
      : 0;
  const manufacturer = HARDWARE_MAP["nvidia"];

  return (
    <Dialog open={isOpen} modal>
      <DialogContent className="w-full max-w-[560px] gap-0 bg-background p-0" showCloseIcon={false}>
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="title-h5 mb-2 text-left text-foreground">
            <div className="flex items-center gap-3">
              {manufacturer?.hardwareManufacturerIcon ? (
                <manufacturer.hardwareManufacturerIcon
                  className={cn("h-[24px] w-[24px]", manufacturer.hardwareManufacturerColor)}
                />
              ) : null}
              <div className={cn("label-medium truncate font-medium leading-6 text-foreground")}>
                {hardware.hardwareName}
              </div>
              <div className="label-x-small rounded-[4px] bg-container px-[6px] py-[2px] font-medium leading-4 text-foreground dark:bg-gray-light-1800">{`x${hardware.numCards}`}</div>
              <div className="flex-1" />
              {hardware.isSoldOut ? (
                <div className="label-x-small rounded-[4px] bg-container px-2 py-[2px] font-medium leading-[130%] text-[#EF4444] dark:bg-[#301414]">
                  Sold Out
                </div>
              ) : null}
              {hardware.isConfidentialCompute && <ConfidentialComputeBadge size="medium" />}
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className=" flex flex-col gap-4 px-6 pb-6">
          <div className="mt-2 flex flex-col gap-1">
            <div className="label-small leading-5 text-gray-dark-2200">Price</div>
            <div className="flex items-baseline gap-3">
              {discount > 0 ? (
                <div className="label-small flex-shrink-0 rounded-[4px] bg-[#00ac4d3d] px-2 py-[2px] font-medium leading-[130%] text-[#00C15D]">
                  -{discount}%
                </div>
              ) : null}
              {hardware.oldPrice && hardware.oldPrice > hardware.price ? (
                <div className="label-medium text-gray-dark-2200 line-through">
                  ${hardware.oldPrice.toFixed(2)}/hr
                </div>
              ) : null}
              <div className="label-medium text-foreground">${hardware.price.toFixed(2)}/hr</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Field label="VRAM Per Card" value={`${hardware.vramPerCard} GB`} />
            {!hiddenHardwareFields.includes("interconnect") && (
              <Field label="Interconnect" value={hardware.interconnect || "-"} />
            )}
            {!hiddenHardwareFields.includes("nvlink") && (
              <Field label="NVLink Support" value={hardware.nvlink ? "Yes" : "No"} />
            )}
            {!hiddenHardwareFields.includes("storage") && (
              <Field label="Available Storage" value={`${hardware.storage} GB`} />
            )}
            {!hiddenHardwareFields.includes("vCPU") && (
              <Field label="VCPUs" value={hardware.vCPU ? String(hardware.vCPU) : "-"} />
            )}
            {!hiddenHardwareFields.includes("memory") && (
              <Field label="Memory" value={hardware.memory ? `${hardware.memory} GB` : "-"} />
            )}
            {!hiddenHardwareFields.includes("supplier") && (
              <Field label="Supplier" value={hardware.supplier === "external" ? "Partner" : "IO"} />
            )}
            <div className="flex flex-col gap-1">
              <div className="label-small leading-5 text-gray-dark-2200">Location</div>
              <CountryFlagLabel code={hardware.location} name={hardware.location} />
            </div>
          </div>
          <div className="mt-2 flex justify-end">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="label-small leading-5 text-gray-dark-2200">{label}</div>
      <div className="subtext-medium font-medium leading-6 text-foreground">{value}</div>
    </div>
  );
};
